/**
 * `useSectionSaveLifecycle` — the ONE implementation of a single section's
 * save lifecycle (ADR-20261001-evn2), extracted out of
 * `MultiSectionStepRoute.tsx` so the two dedicated single-section routes
 * TG-15/TG-16 add (`confirm-details`, `change-check`) and the generic
 * multi-section renderer that still serves `exposures` all consume the
 * SAME state machine rather than three drifting copies of it.
 *
 * Owns, per section:
 *   - the double-write guard (OBJ-65/OBJ-66): a Succeeded write holds its
 *     section closed to a second confirm until the caller's own re-read
 *     (a fresh `confirmedAnswers`) actually reflects it.
 *   - the change-answer-after-confirm flow (OBJ-101/OBJ-102): a confirmed
 *     section can be reopened for editing, and a second write from that
 *     reopen is guarded by the exact same caller-catch-up rule.
 *   - stale/reload-failure gating (OBJ-112/OBJ-114/OBJ-116, AC-9): while
 *     the caller's own post-write re-read has failed, no further write is
 *     offered against the known-stale snapshot.
 *   - `confirmedAnswersMatch` / validation gating: a field's error is
 *     surfaced only once THIS section's own confirm has been attempted
 *     (AC-10's "at the point the customer tries to proceed"), and a
 *     confirm is refused outright while any of its fields are in error.
 *
 * Deliberately does NOT own: which fields fail validation and why (every
 * route injects its own `computeFieldError`, so this module carries no
 * exposure- or change-check-specific field name), and capability
 * (ADR-20260826-ejck: only `confirmableSectionIds`/`useCapability()` are
 * permitted in the route component, never here) — both stay the calling
 * route's concern, same boundary `MultiSectionStepRoute.tsx` already
 * documented before this extraction.
 */
import { useMemo, useState } from "react";
import type { ConfirmedAnswers, SectionInstanceId } from "../../routing/route-table";
import type { WorkingCopyPort } from "../../state/working-copy-port";
import { confirmSection } from "./confirm";

export type SectionLifecycleState = "open" | "confirmed";

export type SaveOutcomeState =
  | {
      readonly sectionId: SectionInstanceId;
      readonly tone: "error";
      readonly title: string;
      readonly message: string;
    }
  | null;

export interface UseSectionSaveLifecycleParams {
  readonly renewalId: string;
  readonly port: WorkingCopyPort;
  readonly confirmedAnswers: ConfirmedAnswers;
  /** OBJ-112/OBJ-114/OBJ-116, AC-9: true whenever the caller's own
   * post-write (or pre-mount) reload has failed and the `confirmedAnswers`/
   * other props this hook was called with are a known-stale snapshot. No
   * further write is offered against stale data -- see `canConfirmBase`
   * and `canRequestChangeBase` on the returned lifecycle below. */
  readonly stale: boolean;
  /** m1-portal-ux-fixes Fix C: the calling screen's own primary action
   * control's visible text (e.g. "Continue", "Confirm this section"),
   * threaded straight through to `confirmSection`'s `actionLabel` — never
   * hardcoded here, since `ConfirmDetailsRoute` and `MultiSectionStepRoute`
   * (this hook's two callers) name their own controls differently. */
  readonly actionLabel: string;
  readonly onSectionConfirmed: (sectionId: SectionInstanceId, answers: Record<string, unknown>) => void;
  /** OBJ-106: called instead of `onSectionConfirmed` when a write comes
   * back Indeterminate -- the server may or may not have committed it, so
   * unlike `onSectionConfirmed` this never folds `answers` into the
   * caller's `confirmedAnswers`. */
  readonly onSectionWriteIndeterminate: (sectionId: SectionInstanceId) => void;
  /** m1-portal-ux-fixes Fix D (DEBT-20261023-a7zv): called instead of
   * setting `outcome` when the write comes back refused because the
   * renewal is already submitted (`confirmSection`'s own `RenewalSubmitted`
   * result) — the caller routes the session to `/renewal-status`, never
   * rendering this as an ordinary save failure. */
  readonly onRenewalSubmitted: () => void;
  /** Field-level validation, injected by the caller: this module owns the
   * GATING (confirmAttempted, "every field clean before a write is
   * attempted") but never the specific rule for a specific field --
   * confirm-details, change-check and exposures each validate a
   * different, named set of fields. Return `undefined` for a field with
   * no error. */
  readonly computeFieldError: (sectionId: SectionInstanceId, fieldId: string, value: string) => string | undefined;
  /** DEBT-20261006-71bn: sections that must already be reopened, with their
   * editable fields already seeded from the SAVED answer, on the very first
   * commit. Read once, at mount, by lazy `useState` initialisers -- later
   * changes to this value are ignored, same as the mount-only effect it
   * replaces. Seeding in a passive effect left a first, non-interactive
   * commit whose reseed silently overwrote any input dispatched before it
   * ran. */
  readonly initialReopen?: readonly InitialReopenSection[] | undefined;
}

export interface InitialReopenSection {
  readonly sectionId: SectionInstanceId;
  readonly fields: readonly string[];
}

export interface SectionLifecycle {
  readonly fieldValue: (fieldId: string) => string;
  readonly setFieldValue: (fieldId: string, value: string) => void;
  /** `undefined` until this section's own confirm has been attempted once
   * (AC-10) -- never shown while the customer is still typing. */
  readonly fieldError: (fieldId: string) => string | undefined;
  /** Reads a field's SAVED value (for a "confirmed" section's read-only
   * display, or to seed a reopen's editable state) -- see
   * `readSavedFieldValue`'s own doc comment for the one named exception
   * (the change-check section's raw scalar answer). */
  readonly displayValue: (fieldId: string) => string;
  readonly isSaving: boolean;
  /** Only ever non-null for the section it names -- callers do not need
   * to re-filter by `sectionId` themselves. */
  readonly outcome: SaveOutcomeState;
  /** OBJ-101/OBJ-102: true while this section (otherwise "confirmed") is
   * being edited again via `onRequestChange` below, until the caller's
   * `confirmedAnswers` reflects the new write. Presentation-layer only --
   * see `MultiSectionStepRoute.tsx`'s own doc comment on why this never
   * alters what `section-state.ts` itself calls "confirmed". */
  readonly isReopened: boolean;
  /** Structural eligibility only -- open or reopened, not blocked by the
   * double-write guard, not stale. Capability is the caller's own
   * decision to AND this with (ADR-20260826-ejck: never duplicated here). */
  readonly canConfirmBase: boolean;
  readonly onConfirm: () => void;
  /** Structural eligibility only -- confirmed, not already reopened, not
   * stale. Same capability boundary as `canConfirmBase`. */
  readonly canRequestChangeBase: boolean;
  readonly onRequestChange: () => void;
}

export interface SectionSaveLifecycle {
  /** Builds the per-section view model for one visible (non-locked)
   * section. Safe to call for every visible section on every render --
   * this performs no I/O and schedules no state update itself; it only
   * reads the lifecycle state this hook already holds. */
  readonly sectionFor: (
    sectionId: SectionInstanceId,
    fields: readonly string[],
    state: SectionLifecycleState,
  ) => SectionLifecycle;
}

/**
 * Reads one field's SAVED value for display. `route-table.ts`'s own doc
 * comment on `ConfirmedAnswers` names one exception to "a section's own
 * capture component owns [the confirmed value's] shape": the change-check
 * section's confirmed value is the raw "yes"/"no" answer itself, not a
 * fields record, because `route-table.ts`'s own `isApplicable` predicates
 * read it directly. `hasAnythingChanged` only ever appears as that one
 * section's only field, so keying this off the field name reaches the
 * same section without a second, sectionId-keyed branch.
 */
export function readSavedFieldValue(confirmedAnswers: ConfirmedAnswers, sectionId: SectionInstanceId, fieldId: string): string {
  const raw = confirmedAnswers[sectionId];
  if (fieldId === "hasAnythingChanged") {
    return typeof raw === "string" ? raw : "";
  }
  const record = (raw ?? {}) as Record<string, unknown>;
  const value = record[fieldId];
  // Only string/number values stringify meaningfully -- anything else (an
  // accidentally nested object, for example) falls back to "" rather than
  // the "[object Object]" a bare String(value) would silently render.
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

/**
 * OBJ-102: true once `confirmedAnswers` reflects every field in `written`
 * (what a confirm actually submitted) -- a deep-enough equality for this
 * purpose because `written`'s values are always the strings a section's
 * form collected, and `readSavedFieldValue` already knows how to read
 * either a raw-string section (`hasAnythingChanged`) or a fields record
 * back out of `confirmedAnswers` for comparison. Mere key presence
 * (`confirmedAnswers[sectionId] !== undefined`) is not enough for a
 * REOPENED section, which already had a key before the new write.
 */
export function confirmedAnswersMatch(
  confirmedAnswers: ConfirmedAnswers,
  sectionId: SectionInstanceId,
  written: Record<string, unknown>,
): boolean {
  return Object.entries(written).every(
    ([fieldId, value]) => readSavedFieldValue(confirmedAnswers, sectionId, fieldId) === value,
  );
}

export function useSectionSaveLifecycle(params: UseSectionSaveLifecycleParams): SectionSaveLifecycle {
  const {
    renewalId,
    port,
    confirmedAnswers,
    stale,
    actionLabel,
    onSectionConfirmed,
    onSectionWriteIndeterminate,
    onRenewalSubmitted,
    computeFieldError,
    initialReopen,
  } = params;

  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const seeded: Record<string, string> = {};
    for (const { sectionId, fields } of initialReopen ?? []) {
      for (const fieldId of fields) {
        seeded[`${sectionId}.${fieldId}`] = readSavedFieldValue(confirmedAnswers, sectionId, fieldId);
      }
    }
    return seeded;
  });
  const [isSavingAny, setIsSavingAny] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<SaveOutcomeState>(null);
  // OBJ-65/OBJ-66: a Succeeded write's `confirmedAnswers` update comes from
  // the CALLER re-rendering (a server re-read in the real composition) --
  // never synchronous with the write settling. This set names every
  // section whose write has Succeeded but whose answer `confirmedAnswers`
  // does not yet contain, so a second activation of the same confirm
  // control cannot issue a second write for the same section.
  const [pendingConfirmedIds, setPendingConfirmedIds] = useState<ReadonlySet<SectionInstanceId>>(new Set());
  const effectivePendingConfirmedIds = useMemo(
    () => new Set(Array.from(pendingConfirmedIds).filter((sectionId) => confirmedAnswers[sectionId] === undefined)),
    [pendingConfirmedIds, confirmedAnswers],
  );
  // AC-10: a section's validation errors are computed live but never
  // SHOWN until its own confirm control has actually been activated once.
  const [confirmAttempted, setConfirmAttempted] = useState<Partial<Record<SectionInstanceId, boolean>>>({});

  // OBJ-101/OBJ-102: presentation-layer override only -- section-state.ts'
  // own "confirmed" (purely "this id is in confirmedAnswers") is untouched
  // and stays correct for every other consumer; only THIS hook's own
  // rendering of a reopened section's content changes.
  const [reopenedSectionIds, setReopenedSectionIds] = useState<ReadonlySet<SectionInstanceId>>(
    () => new Set((initialReopen ?? []).map(({ sectionId }) => sectionId)),
  );
  // OBJ-102: holds the NEW answers a reopened section's confirm just
  // wrote, so the derived set below can tell "the caller's re-read has
  // landed" apart from "this id happens to already be confirmed" by
  // comparing against what was actually submitted rather than mere key
  // presence.
  const [pendingReopenAnswers, setPendingReopenAnswers] = useState<Partial<Record<SectionInstanceId, Record<string, unknown>>>>(
    {},
  );
  const effectiveReopenedSectionIds = useMemo(
    () =>
      new Set(
        Array.from(reopenedSectionIds).filter((sectionId) => {
          const written = pendingReopenAnswers[sectionId];
          return written === undefined || !confirmedAnswersMatch(confirmedAnswers, sectionId, written);
        }),
      ),
    [reopenedSectionIds, pendingReopenAnswers, confirmedAnswers],
  );

  function fieldValue(sectionId: SectionInstanceId, fieldId: string): string {
    return fieldValues[`${sectionId}.${fieldId}`] ?? "";
  }

  function setFieldValue(sectionId: SectionInstanceId, fieldId: string, value: string) {
    setFieldValues((current) => ({ ...current, [`${sectionId}.${fieldId}`]: value }));
  }

  function sectionHasErrors(sectionId: SectionInstanceId, fields: readonly string[]): boolean {
    return fields.some((fieldId) => computeFieldError(sectionId, fieldId, fieldValue(sectionId, fieldId)) !== undefined);
  }

  async function handleConfirm(sectionId: SectionInstanceId, fields: readonly string[]) {
    setConfirmAttempted((current) => ({ ...current, [sectionId]: true }));

    if (sectionHasErrors(sectionId, fields)) {
      // AC-10: named individually on screen next to the offending field --
      // no write is attempted and the customer's entered answers stay
      // exactly as they are, same as a Failed/Indeterminate save outcome.
      return;
    }

    setIsSavingAny(true);
    setSaveOutcome(null);

    const answers: Record<string, unknown> = {};
    for (const fieldId of fields) {
      answers[fieldId] = fieldValue(sectionId, fieldId);
    }

    const result = await confirmSection(port, renewalId, sectionId, answers, actionLabel);
    setIsSavingAny(false);

    if (result.status === "RenewalSubmitted") {
      // Fix D (DEBT-20261023-a7zv): never rendered as a save outcome -- the
      // caller routes this session to `/renewal-status` instead.
      onRenewalSubmitted();
      return;
    }

    if (result.status === "Succeeded") {
      // Hold this section closed to further confirmation until the
      // CALLER's re-render actually reflects it in `confirmedAnswers` --
      // otherwise the still-stale section state re-enables the same
      // confirm control for the whole re-read window and a second click
      // double-writes (OBJ-65/OBJ-66).
      setPendingConfirmedIds((current) => new Set(current).add(sectionId));
      // OBJ-102: `reopenedSectionIds` itself is left alone -- recording
      // what THIS confirm wrote lets `effectiveReopenedSectionIds` keep
      // the reopen rendering live until `confirmedAnswers` actually
      // reflects it, rather than falling back to the stale "confirmed"
      // read for the whole re-read window.
      if (reopenedSectionIds.has(sectionId)) {
        setPendingReopenAnswers((current) => ({ ...current, [sectionId]: answers }));
      }
      onSectionConfirmed(sectionId, answers);
      return;
    }

    if (result.status === "Indeterminate") {
      // OBJ-106: the server may have committed this write even though the
      // response didn't confirm it -- a settled declaration claim
      // certified the working copy as it stood before this attempt, so it
      // must be invalidated here exactly as a Succeeded write invalidates
      // it. Never folded into `confirmedAnswers`: this hook does not know
      // whether the write actually landed.
      onSectionWriteIndeterminate(sectionId);
    }

    setSaveOutcome({ sectionId, tone: result.tone, title: result.title, message: result.message });
  }

  /** OBJ-101: seeds this section's own editable field state from the
   * SAVED answer (the only place a reopened section's starting values can
   * legitimately come from), and clears any stale validation/error state
   * left over from a previous attempt on this section. */
  function handleRequestChange(sectionId: SectionInstanceId, fields: readonly string[]) {
    setFieldValues((current) => {
      const next = { ...current };
      for (const fieldId of fields) {
        next[`${sectionId}.${fieldId}`] = readSavedFieldValue(confirmedAnswers, sectionId, fieldId);
      }
      return next;
    });
    setConfirmAttempted((current) => ({ ...current, [sectionId]: false }));
    setSaveOutcome(null);
    setReopenedSectionIds((current) => new Set(current).add(sectionId));
    // OBJ-102 iteration 2: a PRIOR reopen of this same section may have
    // left a stale entry here -- cleared so a new reopen always starts
    // with no pending-write record of its own, exactly like the
    // section's very first reopen.
    setPendingReopenAnswers((current) => {
      if (current[sectionId] === undefined) {
        return current;
      }
      const next = { ...current };
      delete next[sectionId];
      return next;
    });
  }

  function sectionFor(sectionId: SectionInstanceId, fields: readonly string[], state: SectionLifecycleState): SectionLifecycle {
    const isPendingConfirm = effectivePendingConfirmedIds.has(sectionId);
    const isReopened = state === "confirmed" && effectiveReopenedSectionIds.has(sectionId);
    const isReopenConfirmPending = isReopened && pendingReopenAnswers[sectionId] !== undefined;

    return {
      fieldValue: (fieldId) => fieldValue(sectionId, fieldId),
      setFieldValue: (fieldId, value) => {
        setFieldValue(sectionId, fieldId, value);
      },
      fieldError: (fieldId) =>
        confirmAttempted[sectionId] === true
          ? computeFieldError(sectionId, fieldId, fieldValue(sectionId, fieldId))
          : undefined,
      displayValue: (fieldId) => readSavedFieldValue(confirmedAnswers, sectionId, fieldId),
      isSaving: isSavingAny || isPendingConfirm || isReopenConfirmPending,
      outcome: saveOutcome !== null && saveOutcome.sectionId === sectionId ? saveOutcome : null,
      isReopened,
      // OBJ-112/OBJ-114/OBJ-116, AC-9: stale excludes a write just as
      // firmly as the double-write guard does -- no further write is
      // offered against a snapshot known not to be current.
      canConfirmBase: (state === "open" || isReopened) && !isPendingConfirm && !isReopenConfirmPending && !stale,
      onConfirm: () => {
        void handleConfirm(sectionId, fields);
      },
      canRequestChangeBase: state === "confirmed" && !isReopened && !stale,
      onRequestChange: () => {
        handleRequestChange(sectionId, fields);
      },
    };
  }

  return { sectionFor };
}
